import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { 
  ArrowLeft, 
  ArrowRight, 
  Search, 
  MapPin, 
  Clock, 
  Phone, 
  Globe,
  Upload,
  FileText,
  Camera,
  Link as LinkIcon,
  DollarSign,
  CreditCard,
  Smartphone,
  Tablet,
  Printer,
  CheckCircle,
  Loader2,
  ChefHat,
  Star,
  Building2,
  User,
  Store
} from "lucide-react";

// Types for onboarding data
interface RestaurantSearchResult {
  id?: string;
  name: string;
  address: string;
  city: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  website?: string;
  googlePlaceId?: string;
  source: 'local' | 'google';
  distance?: number;
  category?: string;
  rating?: number;
}

interface OnboardingData {
  // Step 1: Restaurant search
  selectedRestaurant: RestaurantSearchResult | null;
  
  // Step 2: Restaurant info
  restaurantName: string;
  cuisine: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  website: string;
  hoursOfOperation: {
    [key: string]: { open: string; close: string; closed: boolean };
  };
  
  // Step 3: Owner verification
  isOwner: boolean | null;
  userRelation: 'manager' | 'staff' | 'representative' | 'other' | null;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  
  // Step 4: Menu
  menuUploadType: 'pdf' | 'photo' | 'url' | null;
  menuFiles: File[];
  menuUrl: string;
  
  // Step 5: Pricing
  customerPricing: 'markup' | 'absorb' | null;
  deliveryPricing: 'customer_pays' | 'split' | null;
  
  // Step 6: Payment
  paymentMethod: 'direct' | 'eatzy' | null;
  stripeOption: 'connect' | 'signup' | null;
  
  // Step 7: Order receiving
  deviceOption: 'own_tablet' | 'pos_otter' | 'rental' | null;
  rentalTablet: boolean;
  rentalPrinter: boolean;
}

const defaultHours = {
  monday: { open: '09:00', close: '21:00', closed: false },
  tuesday: { open: '09:00', close: '21:00', closed: false },
  wednesday: { open: '09:00', close: '21:00', closed: false },
  thursday: { open: '09:00', close: '21:00', closed: false },
  friday: { open: '09:00', close: '22:00', closed: false },
  saturday: { open: '10:00', close: '22:00', closed: false },
  sunday: { open: '10:00', close: '20:00', closed: false },
};

const initialData: OnboardingData = {
  selectedRestaurant: null,
  restaurantName: '',
  cuisine: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  website: '',
  hoursOfOperation: defaultHours,
  isOwner: null,
  userRelation: null,
  ownerName: '',
  ownerEmail: '',
  ownerPhone: '',
  menuUploadType: null,
  menuFiles: [],
  menuUrl: '',
  customerPricing: null,
  deliveryPricing: null,
  paymentMethod: null,
  stripeOption: null,
  deviceOption: null,
  rentalTablet: false,
  rentalPrinter: false,
};

const TOTAL_STEPS = 7;

const stepTitles = [
  "Find Your Restaurant",
  "Restaurant Details",
  "Verify Ownership",
  "Upload Menu",
  "Set Pricing",
  "Payment Setup",
  "Order Receiving"
];

export default function OwnerOnboarding() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { isAuthenticated, user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<OnboardingData>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<RestaurantSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Get user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Location access denied or unavailable:', error);
        }
      );
    }
  }, []);

  // Debounced search for restaurants
  const searchRestaurants = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchResults([]);
    try {
      const params = new URLSearchParams({ query });
      if (userLocation) {
        params.append('lat', userLocation.lat.toString());
        params.append('lng', userLocation.lng.toString());
      }

      const response = await fetch(`/api/owner/search-restaurants?${params}`);
      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
      } else {
        setSearchResults([]);
        console.error('Restaurant search returned error:', response.status);
        toast({
          title: 'Search unavailable',
          description: 'Could not reach the search service. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [userLocation, toast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchRestaurants(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchRestaurants]);

  const updateData = (updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 1:
        return data.selectedRestaurant !== null;
      case 2:
        return data.restaurantName.trim() !== '' && data.address.trim() !== '' && data.city.trim() !== '';
      case 3:
        if (!isAuthenticated) return false;
        if (data.isOwner === null) return false;
        if (data.isOwner === false) {
          return data.userRelation !== null && 
            data.ownerName.trim() !== '' && 
            data.ownerEmail.trim() !== '' && 
            data.ownerPhone.trim() !== '';
        }
        return true;
      case 4:
        return data.menuUploadType !== null && (
          data.menuUploadType === 'url' ? data.menuUrl.trim() !== '' : data.menuFiles.length > 0
        );
      case 5:
        return data.customerPricing !== null && data.deliveryPricing !== null;
      case 6:
        if (data.paymentMethod === null) return false;
        if (data.paymentMethod === 'direct') return data.stripeOption !== null;
        return true;
      case 7:
        return data.deviceOption !== null;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // For now, just show success - actual API integration comes later
      toast({
        title: "Application Submitted!",
        description: "We'll review your application and get back to you within 24-48 hours.",
      });
      navigate('/owner/success');
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: "Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const selectRestaurant = (restaurant: RestaurantSearchResult) => {
    updateData({
      selectedRestaurant: restaurant,
      restaurantName: restaurant.name,
      address: restaurant.address,
      city: restaurant.city,
      state: restaurant.state || '',
      zipCode: restaurant.zipCode || '',
      phone: restaurant.phone || '',
      website: restaurant.website || '',
      cuisine: restaurant.category || '',
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <Step1FindRestaurant 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          isSearching={isSearching}
          selectedRestaurant={data.selectedRestaurant}
          onSelect={selectRestaurant}
          userLocation={userLocation}
        />;
      case 2:
        return <Step2RestaurantInfo data={data} updateData={updateData} />;
      case 3:
        return <Step3OwnerVerification 
          isAuthenticated={isAuthenticated} 
          user={user}
          data={data}
          updateData={updateData}
        />;
      case 4:
        return <Step4MenuUpload data={data} updateData={updateData} />;
      case 5:
        return <Step5Pricing data={data} updateData={updateData} />;
      case 6:
        return <Step6Payment data={data} updateData={updateData} />;
      case 7:
        return <Step7OrderReceiving data={data} updateData={updateData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ChefHat className="w-6 h-6 text-primary" />
              <span className="font-semibold text-lg">Restaurant Onboarding</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/owner')}>
              Exit
            </Button>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="border-b bg-card/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Step {currentStep} of {TOTAL_STEPS}</span>
            <span className="text-sm text-muted-foreground">{stepTitles[currentStep - 1]}</span>
          </div>
          <Progress value={(currentStep / TOTAL_STEPS) * 100} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4">
            {stepTitles.map((title, index) => (
              <div 
                key={index}
                className={cn(
                  "flex flex-col items-center",
                  index + 1 === currentStep && "text-primary",
                  index + 1 < currentStep && "text-green-500",
                  index + 1 > currentStep && "text-muted-foreground"
                )}
              >
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2",
                  index + 1 === currentStep && "border-primary bg-primary text-primary-foreground",
                  index + 1 < currentStep && "border-green-500 bg-green-500 text-white",
                  index + 1 > currentStep && "border-muted-foreground/30"
                )}>
                  {index + 1 < currentStep ? <CheckCircle className="w-4 h-4" /> : index + 1}
                </div>
                <span className="text-xs mt-1 hidden md:block max-w-[80px] text-center truncate">
                  {title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {renderStepContent()}
        </div>
      </div>

      {/* Navigation */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-card p-4 safe-area-padding-bottom">
        <div className="container mx-auto max-w-2xl flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={!canProceed() || isLoading}
            className="gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : currentStep === TOTAL_STEPS ? (
              <>
                Submit Application
                <CheckCircle className="w-4 h-4" />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Bottom padding for fixed nav */}
      <div className="h-24" />
    </div>
  );
}

// Step 1: Find Restaurant
function Step1FindRestaurant({
  searchQuery,
  setSearchQuery,
  searchResults,
  isSearching,
  selectedRestaurant,
  onSelect,
  userLocation
}: {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  searchResults: RestaurantSearchResult[];
  isSearching: boolean;
  selectedRestaurant: RestaurantSearchResult | null;
  onSelect: (r: RestaurantSearchResult) => void;
  userLocation: { lat: number; lng: number } | null;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Find Your Restaurant</h2>
        <p className="text-muted-foreground">
          Search for your restaurant to get started. We'll pull in any existing information we have.
        </p>
      </div>

      {/* Location indicator */}
      {userLocation && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="w-4 h-4 text-primary" />
          <span>Showing results near your location</span>
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder="Enter restaurant name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12"
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Selected restaurant */}
      {selectedRestaurant && (
        <Card className="border-primary">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Store className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{selectedRestaurant.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedRestaurant.address}, {selectedRestaurant.city}
                  </p>
                  {selectedRestaurant.category && (
                    <Badge variant="secondary" className="mt-1 text-xs">
                      {selectedRestaurant.category}
                    </Badge>
                  )}
                </div>
              </div>
              <CheckCircle className="w-5 h-5 text-primary" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search results */}
      {searchResults.length > 0 && !selectedRestaurant && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Select your restaurant:</p>
          {searchResults.map((result, index) => (
            <Card 
              key={index} 
              className="cursor-pointer hover-elevate"
              onClick={() => onSelect(result)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Store className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{result.name}</h3>
                      <Badge variant="outline" className="text-xs">
                        {result.source === 'local' ? 'In our system' : 'Google'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {result.address}, {result.city}
                    </p>
                    {result.distance !== undefined && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {result.distance < 1 
                          ? `${Math.round(result.distance * 1000)}m away` 
                          : `${result.distance.toFixed(1)}km away`
                        }
                      </p>
                    )}
                  </div>
                  {result.rating && (
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                      {result.rating.toFixed(1)}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* No results */}
      {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && !selectedRestaurant && (
        <Card>
          <CardContent className="p-6 text-center">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold mb-1">Restaurant not found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              We couldn't find a match. You can add it as a new restaurant.
            </p>
            <Button 
              variant="outline"
              onClick={() => onSelect({
                name: searchQuery,
                address: '',
                city: '',
                source: 'google'
              })}
            >
              Add "{searchQuery}" as new restaurant
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Step 2: Restaurant Info
function Step2RestaurantInfo({ 
  data, 
  updateData 
}: { 
  data: OnboardingData; 
  updateData: (u: Partial<OnboardingData>) => void;
}) {
  const cuisineOptions = [
    'American', 'Italian', 'Mexican', 'Chinese', 'Japanese', 'Thai', 
    'Indian', 'Mediterranean', 'French', 'Korean', 'Vietnamese', 
    'Greek', 'Caribbean', 'Middle Eastern', 'BBQ', 'Seafood', 'Pizza', 'Other'
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Restaurant Details</h2>
        <p className="text-muted-foreground">
          Confirm and complete your restaurant information
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Restaurant Name</Label>
          <Input
            id="name"
            value={data.restaurantName}
            onChange={(e) => updateData({ restaurantName: e.target.value })}
            placeholder="Your Restaurant Name"
          />
        </div>

        <div>
          <Label htmlFor="cuisine">Cuisine Type</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {cuisineOptions.map(cuisine => (
              <Badge
                key={cuisine}
                variant={data.cuisine === cuisine ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => updateData({ cuisine })}
              >
                {cuisine}
              </Badge>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="address">Street Address</Label>
          <Input
            id="address"
            value={data.address}
            onChange={(e) => updateData({ address: e.target.value })}
            placeholder="123 Main Street"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              value={data.city}
              onChange={(e) => updateData({ city: e.target.value })}
              placeholder="City"
            />
          </div>
          <div>
            <Label htmlFor="state">State</Label>
            <Input
              id="state"
              value={data.state}
              onChange={(e) => updateData({ state: e.target.value })}
              placeholder="State"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="zipCode">ZIP Code</Label>
            <Input
              id="zipCode"
              value={data.zipCode}
              onChange={(e) => updateData({ zipCode: e.target.value })}
              placeholder="12345"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={data.phone}
              onChange={(e) => updateData({ phone: e.target.value })}
              placeholder="(555) 123-4567"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="website">Website (optional)</Label>
          <Input
            id="website"
            value={data.website}
            onChange={(e) => updateData({ website: e.target.value })}
            placeholder="https://yourrestaurant.com"
          />
        </div>

        {/* Hours of operation - simplified for now */}
        <div>
          <Label className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Hours of Operation
          </Label>
          <p className="text-sm text-muted-foreground mt-1 mb-3">
            You can customize these later in your dashboard
          </p>
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2 text-sm">
                {Object.entries(data.hoursOfOperation).map(([day, hours]) => (
                  <div key={day} className="flex items-center justify-between">
                    <span className="capitalize font-medium">{day}</span>
                    <span className="text-muted-foreground">
                      {hours.closed ? 'Closed' : `${hours.open} - ${hours.close}`}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Step 3: Owner Verification
function Step3OwnerVerification({ 
  isAuthenticated,
  user,
  data,
  updateData
}: { 
  isAuthenticated: boolean;
  user: any;
  data: OnboardingData;
  updateData: (u: Partial<OnboardingData>) => void;
}) {
  const [, navigate] = useLocation();

  const relationOptions = [
    { value: 'manager', label: 'Manager' },
    { value: 'staff', label: 'Staff Member' },
    { value: 'representative', label: 'Authorized Representative' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Verify Ownership</h2>
        <p className="text-muted-foreground">
          Confirm your relationship with this restaurant
        </p>
      </div>

      {isAuthenticated ? (
        <>
          <Card className="border-green-500/50 bg-green-500/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-700 dark:text-green-400">Logged In</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    You're signed in as <strong>{user?.email || user?.name || 'User'}</strong>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div>
            <p className="font-medium mb-3">Are you the owner of {data.restaurantName || 'this restaurant'}?</p>
            <div className="grid grid-cols-2 gap-3">
              <Card 
                className={cn("cursor-pointer hover-elevate", data.isOwner === true && "border-primary")}
                onClick={() => updateData({ isOwner: true, userRelation: null, ownerName: '', ownerEmail: '', ownerPhone: '' })}
              >
                <CardContent className="p-4 text-center">
                  <CheckCircle className={cn("w-8 h-8 mx-auto mb-2", data.isOwner === true ? "text-primary" : "text-muted-foreground")} />
                  <p className="font-medium">Yes, I'm the owner</p>
                </CardContent>
              </Card>
              <Card 
                className={cn("cursor-pointer hover-elevate", data.isOwner === false && "border-primary")}
                onClick={() => updateData({ isOwner: false })}
              >
                <CardContent className="p-4 text-center">
                  <User className={cn("w-8 h-8 mx-auto mb-2", data.isOwner === false ? "text-primary" : "text-muted-foreground")} />
                  <p className="font-medium">No, I'm not the owner</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {data.isOwner === false && (
            <Card>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="mb-2 block">What is your relationship to this restaurant?</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {relationOptions.map(option => (
                      <Badge
                        key={option.value}
                        variant={data.userRelation === option.value ? "default" : "outline"}
                        className="cursor-pointer justify-center py-2"
                        onClick={() => updateData({ userRelation: option.value as any })}
                      >
                        {option.label}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="font-medium mb-3">Owner Contact Information</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Please provide the restaurant owner's details so they can create an account
                  </p>
                  
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="ownerName">Owner's Full Name</Label>
                      <Input
                        id="ownerName"
                        value={data.ownerName}
                        onChange={(e) => updateData({ ownerName: e.target.value })}
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ownerEmail">Owner's Email</Label>
                      <Input
                        id="ownerEmail"
                        type="email"
                        value={data.ownerEmail}
                        onChange={(e) => updateData({ ownerEmail: e.target.value })}
                        placeholder="owner@restaurant.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ownerPhone">Owner's Phone</Label>
                      <Input
                        id="ownerPhone"
                        type="tel"
                        value={data.ownerPhone}
                        onChange={(e) => updateData({ ownerPhone: e.target.value })}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {data.isOwner === true && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-sm">
                    <p className="font-medium">What happens next?</p>
                    <ul className="mt-2 space-y-1 text-muted-foreground">
                      <li>• We'll verify your ownership claim</li>
                      <li>• You'll receive an email confirmation</li>
                      <li>• Once approved, you can manage your restaurant</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-6 text-center">
            <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Sign in to continue</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You need to be signed in to claim ownership of a restaurant
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/login?redirect=/owner/onboarding')}>
                Sign In
              </Button>
              <Button variant="outline" onClick={() => navigate('/signup?redirect=/owner/onboarding')}>
                Create Account
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Step 4: Menu Upload
function Step4MenuUpload({ 
  data, 
  updateData 
}: { 
  data: OnboardingData; 
  updateData: (u: Partial<OnboardingData>) => void;
}) {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      updateData({ menuFiles: Array.from(e.target.files) });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Upload Your Menu</h2>
        <p className="text-muted-foreground">
          Share your menu so we can set up online ordering
        </p>
      </div>

      <div className="space-y-4">
        <p className="text-sm font-medium">How would you like to share your menu?</p>
        
        <div className="grid gap-4">
          <Card 
            className={cn(
              "cursor-pointer hover-elevate",
              data.menuUploadType === 'pdf' && "border-primary"
            )}
            onClick={() => updateData({ menuUploadType: 'pdf' })}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className={cn(
                "w-12 h-12 rounded-lg flex items-center justify-center",
                data.menuUploadType === 'pdf' ? "bg-primary text-primary-foreground" : "bg-muted"
              )}>
                <FileText className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Upload PDF</h3>
                <p className="text-sm text-muted-foreground">Upload a PDF version of your menu</p>
              </div>
              {data.menuUploadType === 'pdf' && <CheckCircle className="w-5 h-5 text-primary" />}
            </CardContent>
          </Card>

          <Card 
            className={cn(
              "cursor-pointer hover-elevate",
              data.menuUploadType === 'photo' && "border-primary"
            )}
            onClick={() => updateData({ menuUploadType: 'photo' })}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className={cn(
                "w-12 h-12 rounded-lg flex items-center justify-center",
                data.menuUploadType === 'photo' ? "bg-primary text-primary-foreground" : "bg-muted"
              )}>
                <Camera className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Upload Photos</h3>
                <p className="text-sm text-muted-foreground">Take photos of your physical menu</p>
              </div>
              {data.menuUploadType === 'photo' && <CheckCircle className="w-5 h-5 text-primary" />}
            </CardContent>
          </Card>

          <Card 
            className={cn(
              "cursor-pointer hover-elevate",
              data.menuUploadType === 'url' && "border-primary"
            )}
            onClick={() => updateData({ menuUploadType: 'url' })}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className={cn(
                "w-12 h-12 rounded-lg flex items-center justify-center",
                data.menuUploadType === 'url' ? "bg-primary text-primary-foreground" : "bg-muted"
              )}>
                <LinkIcon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Link to Online Menu</h3>
                <p className="text-sm text-muted-foreground">Provide a URL to your existing online menu</p>
              </div>
              {data.menuUploadType === 'url' && <CheckCircle className="w-5 h-5 text-primary" />}
            </CardContent>
          </Card>
        </div>

        {/* Upload area based on selection */}
        {data.menuUploadType === 'pdf' && (
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium mb-1">Drag and drop your PDF here</p>
            <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
            <Input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="hidden"
              id="pdf-upload"
            />
            <Label htmlFor="pdf-upload">
              <Button variant="outline" asChild>
                <span>Choose File</span>
              </Button>
            </Label>
            {data.menuFiles.length > 0 && (
              <p className="text-sm text-green-600 mt-3">
                {data.menuFiles.length} file(s) selected
              </p>
            )}
          </div>
        )}

        {data.menuUploadType === 'photo' && (
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <Camera className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium mb-1">Upload menu photos</p>
            <p className="text-sm text-muted-foreground mb-4">You can upload multiple images</p>
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
              id="photo-upload"
            />
            <Label htmlFor="photo-upload">
              <Button variant="outline" asChild>
                <span>Choose Photos</span>
              </Button>
            </Label>
            {data.menuFiles.length > 0 && (
              <p className="text-sm text-green-600 mt-3">
                {data.menuFiles.length} photo(s) selected
              </p>
            )}
          </div>
        )}

        {data.menuUploadType === 'url' && (
          <div>
            <Label htmlFor="menuUrl">Menu URL</Label>
            <Input
              id="menuUrl"
              value={data.menuUrl}
              onChange={(e) => updateData({ menuUrl: e.target.value })}
              placeholder="https://yourrestaurant.com/menu"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Step 5: Pricing
function Step5Pricing({ 
  data, 
  updateData 
}: { 
  data: OnboardingData; 
  updateData: (u: Partial<OnboardingData>) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Set Your Pricing</h2>
        <p className="text-muted-foreground">
          Choose how you want to charge your customers
        </p>
      </div>

      <div className="space-y-4">
        <p className="font-medium">How would you like to charge your customers?</p>
        <RadioGroup 
          value={data.customerPricing || ''} 
          onValueChange={(v) => updateData({ customerPricing: v as 'markup' | 'absorb' })}
        >
          <Card className={cn(data.customerPricing === 'markup' && "border-primary")}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <RadioGroupItem value="markup" id="markup" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="markup" className="font-semibold cursor-pointer">
                    Add 5% to the menu price
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Online prices will be 5% higher than your in-store prices. You pay nothing extra.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(data.customerPricing === 'absorb' && "border-primary")}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <RadioGroupItem value="absorb" id="absorb" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="absorb" className="font-semibold cursor-pointer">
                    I'll pay the 5%, don't change the menu price
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Keep online prices the same as in-store. The 5% fee will be deducted from your earnings.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </RadioGroup>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <p className="font-medium">How would you like to charge for delivery?</p>
        <RadioGroup 
          value={data.deliveryPricing || ''} 
          onValueChange={(v) => updateData({ deliveryPricing: v as 'customer_pays' | 'split' })}
        >
          <Card className={cn(data.deliveryPricing === 'customer_pays' && "border-primary")}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <RadioGroupItem value="customer_pays" id="customer_pays" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="customer_pays" className="font-semibold cursor-pointer">
                    Customers pay a base $6.99 delivery fee
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    The full delivery fee is charged to customers. Standard pricing for most restaurants.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(data.deliveryPricing === 'split' && "border-primary")}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <RadioGroupItem value="split" id="split" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label htmlFor="split" className="font-semibold cursor-pointer">
                      Split the fee. Charge the customer $3.99, I'll pay $3.00 to the driver.
                    </Label>
                    <Badge className="bg-green-500 text-white">+68% delivery volume</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Lower customer fees increase order volume significantly. You cover $3.00 per delivery.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </RadioGroup>
      </div>
    </div>
  );
}

// Step 6: Payment
function Step6Payment({ 
  data, 
  updateData 
}: { 
  data: OnboardingData; 
  updateData: (u: Partial<OnboardingData>) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Payment Setup</h2>
        <p className="text-muted-foreground">
          Choose how you want to receive payments
        </p>
      </div>

      <RadioGroup 
        value={data.paymentMethod || ''} 
        onValueChange={(v) => updateData({ paymentMethod: v as 'direct' | 'eatzy', stripeOption: null })}
      >
        <Card className={cn(data.paymentMethod === 'direct' && "border-primary")}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <RadioGroupItem value="direct" id="direct" className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary" />
                  <Label htmlFor="direct" className="font-semibold cursor-pointer">
                    Customer pays me directly, I get paid daily like any other credit card
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Payments go directly to your account. Daily deposits just like regular card transactions.
                </p>
                
                {data.paymentMethod === 'direct' && (
                  <div className="mt-4 space-y-3 pl-4 border-l-2">
                    <p className="text-sm font-medium">How would you like to set up direct payments?</p>
                    <Card 
                      className={cn("cursor-pointer hover-elevate", data.stripeOption === 'connect' && "border-primary")}
                      onClick={() => updateData({ stripeOption: 'connect' })}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Connect my Stripe Account</p>
                            <p className="text-xs text-muted-foreground">I already have a Stripe account</p>
                          </div>
                          {data.stripeOption === 'connect' && <CheckCircle className="w-5 h-5 text-primary" />}
                        </div>
                      </CardContent>
                    </Card>
                    <Card 
                      className={cn("cursor-pointer hover-elevate", data.stripeOption === 'signup' && "border-primary")}
                      onClick={() => updateData({ stripeOption: 'signup' })}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Sign up with Stripe</p>
                            <p className="text-xs text-muted-foreground">Create a new Stripe account to receive payments</p>
                          </div>
                          {data.stripeOption === 'signup' && <CheckCircle className="w-5 h-5 text-primary" />}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(data.paymentMethod === 'eatzy' && "border-primary")}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <RadioGroupItem value="eatzy" id="eatzy" className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  <Label htmlFor="eatzy" className="font-semibold cursor-pointer">
                    Eatzy handles the transaction and pays me weekly
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  We collect payments and send you a weekly deposit. Simple and hands-off.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </RadioGroup>
    </div>
  );
}

// Step 7: Order Receiving
function Step7OrderReceiving({ 
  data, 
  updateData 
}: { 
  data: OnboardingData; 
  updateData: (u: Partial<OnboardingData>) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Receive Orders</h2>
        <p className="text-muted-foreground">
          Choose how you want to receive incoming orders
        </p>
      </div>

      <RadioGroup 
        value={data.deviceOption || ''} 
        onValueChange={(v) => updateData({ deviceOption: v as 'own_tablet' | 'pos_otter' | 'rental' })}
      >
        <Card className={cn(data.deviceOption === 'own_tablet' && "border-primary")}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <RadioGroupItem value="own_tablet" id="own_tablet" className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Tablet className="w-5 h-5 text-primary" />
                    <Label htmlFor="own_tablet" className="font-semibold cursor-pointer">
                      Use my own tablet
                    </Label>
                  </div>
                  <Badge variant="secondary">Free</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Download our app on your existing tablet or smartphone. WiFi or 4G connection required.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(data.deviceOption === 'pos_otter' && "border-primary")}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <RadioGroupItem value="pos_otter" id="pos_otter" className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-primary" />
                    <Label htmlFor="pos_otter" className="font-semibold cursor-pointer">
                      Direct to POS (Otter)
                    </Label>
                  </div>
                  <Badge variant="secondary">Free</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  If you have an Otter subscription, orders go directly to your POS system.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(data.deviceOption === 'rental' && "border-primary")}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <RadioGroupItem value="rental" id="rental" className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Printer className="w-5 h-5 text-primary" />
                  <Label htmlFor="rental" className="font-semibold cursor-pointer">
                    Rent equipment from us
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  We'll provide a 4G tablet and/or printer with your orders.
                </p>
                
                {data.deviceOption === 'rental' && (
                  <div className="mt-4 space-y-3 pl-4 border-l-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="rentalTablet"
                          checked={data.rentalTablet}
                          onCheckedChange={(checked) => updateData({ rentalTablet: !!checked })}
                        />
                        <Label htmlFor="rentalTablet" className="cursor-pointer">
                          4G Tablet
                        </Label>
                      </div>
                      <span className="text-sm font-medium">$30/month</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="rentalPrinter"
                          checked={data.rentalPrinter}
                          onCheckedChange={(checked) => updateData({ rentalPrinter: !!checked })}
                        />
                        <Label htmlFor="rentalPrinter" className="cursor-pointer">
                          Receipt Printer
                        </Label>
                      </div>
                      <span className="text-sm font-medium">$20/month</span>
                    </div>
                    {(data.rentalTablet || data.rentalPrinter) && (
                      <div className="pt-2 border-t">
                        <div className="flex justify-between font-medium">
                          <span>Monthly Total</span>
                          <span>
                            ${(data.rentalTablet ? 30 : 0) + (data.rentalPrinter ? 20 : 0)}/month
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </RadioGroup>
    </div>
  );
}
